import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { toast } from "react-toastify";
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useTranslation } from "react-i18next";
import DoctorAvailabilityModal from '../modals/DoctorAvailabilityModal';
import StandardModal from '../../Modals/standardModal';
import '../styles/DoctorAvailabilityComponent.css';
import { Tooltip } from 'react-tooltip'; 
import FormatUrl from '../../../utils/UrlFormatter';
import { getClinicId } from '../../../utils/config';


const localizer = momentLocalizer(moment);

const DoctorAvailabilityComponent = ({logged_in, user, account, logout}) => {
  const { t } = useTranslation();
  
  const customMessages = {
    month: 'Month',
    week: 'Week',
    day: 'Day',
    agenda: 'Summary', 
  };

  const effectiveClinicId = getClinicId(user);
  

  const navigate = useNavigate();
  // const today = new Date(); 
  // const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const todayStart = useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }, []);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false); 
  const [pastDateModalOpen, setPastDateModalOpen] = useState(false); 
  const [markedSlotsForDay, setMarkedSlotsForDay] = useState([]); 
  // const [todaysAvailability, setTodaysAvailability] = useState([]);
  // const [weeksAvailability, setWeeksAvailability] = useState([]);
  // const [monthsAvailability, setMonthsAvailability] = useState([]);
  // const [activeView, setActiveView] = useState('today'); 
  const [availableDoctors, setAvailableDoctors] = useState([]); 
  const [loadingDoctors, setLoadingDoctors] = useState(false); 
  const [doctorFetchError, setDoctorFetchError] = useState(null); 
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [filteredSlots, setFilteredSlots] = useState([]); 
  const [showMessage, setShowMessage] = useState(false);


  const fetchDoctors = useCallback(async (inputClinicId) => {

    const clinicId = getClinicId(user) ?? inputClinicId;
    if (!clinicId) {
      console.error('Clinic ID is missing.');
      setAvailableDoctors([]);
      setDoctorFetchError('Clinic ID is missing.'); 
      return;
    }

  
    setLoadingDoctors(true); 
    try {
      const csrfUrl = FormatUrl(`/csrf-token/`);
      const csrfResponse = await fetch(csrfUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token.');
  
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      const apiUrl = FormatUrl(`/get-doctors/?clinic_id=${clinicId}`);
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Failed to fetch doctors: ${response.status}`);
  
      const data = await response.json();
      setAvailableDoctors(data.practitioners || []); 
      setDoctorFetchError(null); 
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setAvailableDoctors([]); 
      setDoctorFetchError('Failed to fetch doctors. Please try again.'); 
    } finally {
      setLoadingDoctors(false); 
    }
  }, [user]);
  
  const fetchCsrfToken = async () => {
    try {
      const csrfUrl = FormatUrl('/csrf-token/');
      const csrfResponse = await fetch(csrfUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token.');
      const csrfData = await csrfResponse.json();
      return csrfData.csrfToken;
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      throw error;
    }
  };

  const loadAndUpdateSlots = useCallback(async () => {
    if (!effectiveClinicId) {
      console.error('Clinic ID is missing.');
      return;
    }
  
    try {
      const csrfToken = await fetchCsrfToken();
    
      const apiUrl = FormatUrl(`/get-shifts/?clinic_id=${effectiveClinicId}${selectedPractitioner ? `&practitioner_id=${selectedPractitioner.id}` : ''}`);
    
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });
    
      if (!response.ok) throw new Error(`Failed to fetch shifts: ${response.status}`);
      const data = await response.json();
    
      const fetchedSlots = data.shifts || [];  // Keep variable name same
      // const today = new Date();
    
      const processedSlots = fetchedSlots.map((slot) => {
        const start = new Date(slot.start_time);
        const end = new Date(slot.end_time);
    
        return {
          ...slot,
          title:
          slot.status === 'AV' ? 'Available'
          : slot.status === 'UN' ? 'Unavailable'
          : slot.status === 'CA' ? 'Cancelled'
          : 'Unavailable',
        
          status: slot.status ?? 'AV', 
          start,
          end,
          isPast: end < todayStart,
          practitioner: selectedPractitioner
            ? {
                id: selectedPractitioner.id,
                first_name: selectedPractitioner.name?.split(' ')[0] || '',
                last_name: selectedPractitioner.name?.split(' ').slice(1).join(' ') || '',
              }
            : null,
        };
      });
    
      setAvailableSlots(processedSlots);
    } catch (error) {
      console.error('Error loading shifts:', error);
    }
    
  },[effectiveClinicId, selectedPractitioner, todayStart])
  
  useEffect(() => {
    if (logged_in && effectiveClinicId && selectedPractitioner) {
      loadAndUpdateSlots();
    }
  }, [logged_in, effectiveClinicId, selectedPractitioner, loadAndUpdateSlots]);
  
  
  

  useEffect(() => {
    if (selectedPractitioner) {
      const practitionerSlots = availableSlots.filter(
        (slot) => slot.practitioner?.id === selectedPractitioner.id
      );
      setFilteredSlots(practitionerSlots);
    } else {
      setFilteredSlots(availableSlots);
    }
  }, [selectedPractitioner, availableSlots]);


  // const handleViewChange = (view) => {
  //   setActiveView(view); 
  // };


  // Save slots to localStorage
  // const saveToLocalStorage = (slots) => {
  //   localStorage.setItem('availableSlots', JSON.stringify(slots));
  // };

  const saveSlotsToBackend = async (slots) => {
    if (!effectiveClinicId) {
      console.error('Clinic ID is missing.');
      return;
    }
  
    try {
      const csrfToken = await fetchCsrfToken();
  
      // API URL
      const apiUrl = FormatUrl(`/manage-slot/`);

      // Prepare data using URLSearchParams for form-urlencoded format
      const formData = new URLSearchParams();
      formData.append('clinic_id', effectiveClinicId);
      formData.append('practitioner_id', selectedPractitioner?.id || '');
      formData.append(
        'slots',
        JSON.stringify(
          slots.map(slot => ({
            title: slot.title || 'Available',
            status:slot.status,
            start: slot.start.toISOString(),
            end: slot.end.toISOString(),
          }))
        )
      );
  
      // Send the request
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: formData,
      });
  
      if (!response.ok) throw new Error(`Failed to save slots: ${response.status}`);
      const data = await response.json();
      console.log('Slots saved successfully:', data);
    } catch (error) {
      console.error('Error saving slots to backend:', error);
    }
  };
  
  const cancelSlotFromBackend = async (slot) => {
    try {
      if (!slot?.id || !effectiveClinicId) {
        throw new Error("Missing shift ID or clinic ID.");
      }
  
      const csrfToken = await fetchCsrfToken();
      const apiUrl = FormatUrl(`/cancel-shift/`);
  
      const formData = new URLSearchParams();
      formData.append('clinic_id', effectiveClinicId);
      formData.append('shift_id', JSON.stringify(slot.id));
  
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: formData,
      });
  
      const result = await response.json();
  
      if (!response.ok || result.status !== "success") {
        // Specific backend failure case: appointments exist during shift
        if (result?.error?.includes('not possible to cancel this shift')) {
          toast.warning("Cannot cancel shift: Appointments are already booked in this shift.");
        } else {
          toast.error("Failed to cancel shift. Please try again.");
        }
        return;
      }
  
      toast.success(`Shift at ${new Date(slot.start).toLocaleTimeString()} cancelled successfully!`);
      loadAndUpdateSlots();
  
    } catch (error) {
      console.error(`Error cancelling shift ${slot?.id}:`, error);
      toast.error("Failed to cancel shift. Please try again.");
    }
  };
  
  // Handle slot selection and prevent past date selection
  const handleSlotSelect = (slotInfo) => {
    if (slotInfo.start < todayStart) {
      // Get all the marked slots for this specific past date
      const slotsForDay = availableSlots.filter(
        (slot) => moment(slot.start).isSame(slotInfo.start, 'day')
      );
      
      setMarkedSlotsForDay(slotsForDay); 
      setPastDateModalOpen(true); 
      return; 
    }
    setSelectedSlot(slotInfo);
    setModalOpen(true);
    setIsEditMode(false); 
  };


  // Handle when an existing event (slot) is clicked (for editing)
  const handleEventClick = (event) => {
    setSelectedSlot(event);
    setModalOpen(true);
    setIsEditMode(true);
  };
  


  // Save the selected slot(s) after confirmation in the modal
  const handleSaveSlot =  async (newSlots) => {
    // const today = new Date(); 
    
    if (isEditMode) {
  
        // Editing: Replace the existing slot with the updated one
        const updatedSlots = availableSlots.map(slot => 
            slot.id === selectedSlot.id 
                ? { 
                    ...newSlots[0], 
                    isPast: newSlots[0].end < todayStart, 
                    practitioner: selectedPractitioner,
                    status: selectedSlot.status
                  } // Update the slot and add isPast flag
                : slot // Keep other slots unchanged
        );
  
        setAvailableSlots(updatedSlots);
        // saveToLocalStorage(updatedSlots);
        // Update filteredSlots after saving
        setFilteredSlots(updatedSlots.filter(
            slot => slot.practitioner?.id === selectedPractitioner?.id
        ));
        // Save to backend
      await saveSlotsToBackend(updatedSlots);
    } else {

        // Split name into first_name and last_name
        const [first_name, ...rest] = (selectedPractitioner?.name || "").split(" ");
        const last_name = rest.join(" "); // Combine the remaining parts of the name as last_name

  
        // Adding: Merge newSlots (could be one or many) with existing slots
        const updatedNewSlots = newSlots.map(slot => ({
            ...slot,
            id: selectedPractitioner?.id, 
            isPast: slot.end < todayStart, 
            status: slot.status ,
            title: slot.title || (slot.status === 'AV' ? 'Available' : 'Unavailable'),
            practitioner: {
              id: selectedPractitioner?.id,
              first_name, // Set first_name
              last_name,  // Set last_name
            },
        }));

  
        const updatedSlots = [...availableSlots, ...updatedNewSlots];
        
        setAvailableSlots(updatedSlots);
        // saveToLocalStorage(updatedSlots);
        // Update filteredSlots after saving
        setFilteredSlots(updatedSlots.filter(
            slot => slot.practitioner?.id === selectedPractitioner?.id
        ));
        // Save to backend
        await saveSlotsToBackend(updatedNewSlots);
    }
  
    setModalOpen(false); 
};

  
  // Delete slot logic
  
  const deleteSlotFromList = (slot) => {

    cancelSlotFromBackend(slot)
      .then(() => {
        loadAndUpdateSlots();
      })
      .catch((error) => {
        console.error(`Error cancelling slot ${slot.id}:`, error);
      });
};


  const closeModal = () => {
    setModalOpen(false);
  };


  const closePastDateModal = () => {
    setPastDateModalOpen(false); 
  };

  const eventStyleGetter = (event) => {
  
    let backgroundColor;
  
    // Use status to determine color
    if (event.status === 'AV' || event.title === 'Available') {
      backgroundColor = 'var(--clr-primary-5)';
    } else if (event.status === 'CA' || event.title === 'Cancelled') {
      backgroundColor = '#999999'; // grey tone for cancelled
    } else {
      backgroundColor = 'var(--red-light)';
    }
    
  
    // const today = new Date();
  
    // Style past events differently (greyed out)
    if (event.end < todayStart) {
      return {
        style: {
          backgroundColor: '#C0C0C0',
          color: 'white',
          pointerEvents: 'none',
          opacity: 0.7,
        },
      };
    }
  
    return {
      style: {
        backgroundColor,
        color: 'white',
        borderRadius: '4px',
        padding: '5px',
      },
    };
  };
  

  // Disable past dates visually and functionally
  const dayPropGetter = (date) => {
    if (date < todayStart) {
      return {
        className: 'past-date', 
        style: {
          backgroundColor: '#f0f0f0',
          color: '#b0b0b0',
          pointerEvents: 'none', 
          cursor: 'not-allowed',
        },
      };
    }
    return {};
  };


// Utility method to filter slots based on the selected practitioner
  const filterSlotsByPractitioner = useCallback(() => {
    if ((user.role === 'CO' || user.role === 'CA') && !selectedPractitioner) {
        setFilteredSlots(availableSlots); 
        return;
    }

    if (!selectedPractitioner) {
        setFilteredSlots([]); 
        return;
    }

    const practitionerSlots = availableSlots.filter(
        (slot) => slot.practitioner?.id === selectedPractitioner.id
    );


    setFilteredSlots(practitionerSlots); 
  }, [selectedPractitioner, availableSlots, user.role]);


  useEffect(() => {
    if (!logged_in) {
        setShowMessage(true); 
        const timer = setTimeout(() => {
            navigate('/'); 
        }, 5000);

        return () => clearTimeout(timer); 
    }
}, [logged_in, navigate]);


  // Load saved slots from localStorage on component mount
  // useEffect(() => {
  //   loadAndUpdateSlots();  
  // }, [loadAndUpdateSlots]);  


  useEffect(() => {
    filterSlotsByPractitioner();
  }, [filterSlotsByPractitioner]);


  return (
    <>
      {!logged_in && showMessage ? (
        <div className="landing-page-container">
            <div className="logout-message-container">
                <h2 className="logout-message-title">Please log in to access the Staff Management Portal.</h2>
            </div>
        </div>
          ) : (user.role !== 'CO' && user.role !== 'CA') ? (
                <div className="landing-page-container">
                  <div className="logout-message-container">
                    <h2 className="logout-message-title">Access Restricted.</h2>
                      <p>You do not have permission to access this page.</p>
                    </div>
                </div>
            ) : (
                  <div className="custom-availability-container">
                    <h2 className="custom-availability-header">{t("staff_availability.configure_staff_availability")}</h2>
                    <div className="availability-description">
                    <p>
                    {t("staff_availability.calendar_instructions")}
                    </p>
                    </div>
                    {(user?.role === 'CO' || user?.role === 'CA') && !selectedPractitioner && (
                        <p className="unlock-hint">
                          {t("staff_availability.select_practitioner_prompt")}
                        </p>
                      )}

                      <div className="doctor-fetch-container">
                        {/* Adding the statement on which role is marking the slots */}
                        <div className="doctor-role-message">
                          {(user?.role === 'CO' || user?.role === 'CA') ? (
                            <p>
                              {t("staff_availability.slots_marked_by")} <strong>{t("staff_availability.clinic_admin")}</strong> {t("staff_availability.for_clinic")}: 
                              <span className="clinic-name"> {user?.clinic_name || 'N/A'}</span>
                            </p>
                          ) : user?.role === 'DO' ? (
                            <p>
                              Slots are being marked by the <strong>Practitioner</strong>: 
                              <span className="clinic-name"> {`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'N/A'}</span>
                            </p>
                          ) : (
                            <p className="error-message">{t("staff_availability.invalid_user_role")}</p>
                          )}
                        </div>
                        
                        {/* dropdown addition according to the role */}
                        
                        <div className="role-based-actions">
                          {(user?.role === 'CO' || user?.role === 'CA')&& (
                            <div className="practitioner-selection">
                              <p>{t("staff_availability.select_practitioner_for_slots")}</p>
                              <select
                                className="practitioner-dropdown"
                                onFocus={() => {
                                  if (availableDoctors.length === 0) {
                                    fetchDoctors(effectiveClinicId); 
                                  }
                                }}
                                onChange={(e) => {
                                  const selectedId = e.target.value;

                                  // if (selectedId === 'all') {
                                  //   // Handle "All Marked Slots" selection
                                  //   console.log('All Marked Slots selected. Showing all available slots.');
                                  //   setSelectedPractitioner(null);
                                  //   setFilteredSlots(availableSlots); 
                                  //   return;
                                  // }

                                  const practitioner = availableDoctors.find(
                                    (doctor) => doctor.id.toString() === selectedId
                                  );

                                  if (practitioner) {
                                    const practitionerDetails = {
                                      id: practitioner.id,
                                      name: `${practitioner.firstName} ${practitioner.lastName}`,
                                      email: practitioner.email,
                                      phone: practitioner.phone,
                                      is_registered: practitioner.is_registered,
                                    };

                                    setSelectedPractitioner(practitionerDetails); 

                                    // Refetch slots for the selected practitioner
                                    // loadAndUpdateSlots();
                                    // Filter available slots based on the selected practitioner
                                    const practitionerSlots = availableSlots.filter(
                                      (slot) => slot.practitioner?.id === practitioner.id
                                    );
                                    setFilteredSlots(practitionerSlots); 
                                  } else {
                                    setSelectedPractitioner(null); 
                                    setFilteredSlots([]); 
                                  }
                                }}
                                defaultValue=""
                              >
                                <option value="" disabled>
                                {t("staff_availability.select_practitioner_placeholder")}
                                </option>
                                {/* <option value="all">{t("staff_availability.all_marked_slots")}</option> */}
                                {availableDoctors.map((doctor) => (
                                  <option key={doctor.id} value={doctor.id}>
                                    {doctor.firstName} {doctor.lastName}
                                  </option>
                                ))}
                              </select>
                              {loadingDoctors && <p> {t("staff_availability.loading_practitioners")}</p>}
                              {doctorFetchError && <p className="error-message">Error: {doctorFetchError}</p>}

                             {/* Visual Feedback */}
                              {selectedPractitioner && (
                                <div className="selected-practitioner-details">
                                  <h4>{t("staff_availability.selected_practitioner_details")}</h4>
                                  <p><strong>{t("staff_availability.name")}:</strong> {selectedPractitioner.name}</p>
                                  <p><strong>{t("staff_availability.email")}:</strong> {selectedPractitioner.email}</p>
                                  <p><strong>{t("staff_availability.phone")}:</strong> {selectedPractitioner.phone}</p>
                                  <p>
                                    <strong>{t("staff_availability.license_status")}:</strong>{" "}
                                    {selectedPractitioner.is_registered
                                      ? t("staff_availability.registered_label") 
                                      : t("staff_availability.not_registered_label")}
                                  </p> 
                                </div>
                              )}
                            </div>
                          )}

                          {/* {user?.role === 'DO' && (
                            <p>
                              Slots are being marked by <strong>{user.username || 'Practitioner'}</strong> for the clinic{' '}
                              <strong>{user.clinic_name || 'N/A'}</strong>.
                            </p>
                          )} */}
                        </div>
                      </div>
                      <div className="calendar-legend">
                        <div className="legend-item">
                          <div className="legend-color" style={{ backgroundColor: 'var(--clr-primary-5)' }}></div>
                          <span>{t("staff_availability.marked_available")}</span>
                        </div>
                        <div className="legend-item">
                          <div className="legend-color" style={{ backgroundColor: 'var(--red-light)' }}></div>
                          <span>{t("staff_availability.marked_unavailable")}</span>
                        </div>
                        {/* <div className="legend-item">
                          <div className="legend-color" style={{ backgroundColor: 'var(--blue-light)' }}></div>
                          <span>{t("staff_availability.booked_slots")}</span>
                        </div> */}
                        {/* <div className="legend-item">
                          <div className="legend-color" style={{ backgroundColor: '#999999' }}></div>
                          <span>{t("staff_availability.cancelled_slots")}</span>
                        </div> */}
                        <div className="legend-item">
                          <div className="legend-color" style={{ backgroundColor: '#C0C0C0' }}></div>
                          <span>{t("staff_availability.past_slots")}</span>
                        </div>
                      </div>

                   
                    {/* Calendar */}
                    <Calendar
                      localizer={localizer}
                      events={filteredSlots}
                      selectable={
                        user.role === 'DO' || 
                        ((user.role === 'CO' || user.role === 'CA') && selectedPractitioner !== null)
                      }
                      onSelectSlot={(slotInfo) => {
                        if ((user.role === 'CO' || user.role === 'CA') && !selectedPractitioner) {
                          return;
                        }
                        handleSlotSelect(slotInfo);
                      }}
                      
                      onSelectEvent={(event) => {
                        if ((user.role === 'CO' || user.role === 'CA') && !selectedPractitioner) {
                          return;
                        }
                        handleEventClick(event);
                      }}                      
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: 500 }}
                      className="custom-availability-calendar"
                      views={['month', 'week', 'day', 'agenda']}  // Supported views
                      messages={customMessages}
                      eventPropGetter={eventStyleGetter} // Use custom styling for event slots
                      dayPropGetter={dayPropGetter} // Disable past dates visually
                      components={{
                        event: ({ event }) => (
                          <div>
                            <div
                              data-tooltip-id={`tooltip-${event.start.getTime()}`} // Unique ID for tooltip
                              data-tooltip-content={`Start: ${moment(event.start).format('h:mm A')} - End: ${moment(event.end).format('h:mm A')} | Status: ${event.title}`}
                            >
                              <strong>
                                {event.title === 'Available'
                                  ? `${event.practitioner?.first_name || 'Unknown'} ${event.practitioner?.last_name || ''}`
                                  : `${event.practitioner?.first_name || 'Unknown'} ${event.practitioner?.last_name || ''}`} 
                                {event.end < todayStart ? ' (Past)' : ''}
                              </strong>
                            </div>
                            {/* Tooltip tied to the event's unique ID */}
                            <Tooltip id={`tooltip-${event.start.getTime()}`} place="top" />
                          </div>
                        ),
                      }}
                    />


                    {/* Modal for setting or editing availability */}
                    {modalOpen && effectiveClinicId &&(
                      <DoctorAvailabilityModal
                        slot={selectedSlot}
                        saveSlot={handleSaveSlot}
                        closeModal={closeModal}
                        deleteSlot={deleteSlotFromList}
                        availableSlots={availableSlots}
                        isEdit={isEditMode} 
                        clinicId={effectiveClinicId} 
                        selectedPractitioner={selectedPractitioner} 
                      />
                    )}

                    {/* Standard Modal for past date selection showing marked slots */}
                    <StandardModal
                      isOpen={pastDateModalOpen}
                      onClose={closePastDateModal}
                      title={t("staff_availability.past_marked_slots")}
                      message={
                        <div>
                          <p><strong>{t("staff_availability.cannot_mark_past_dates")}</strong></p>
                          {markedSlotsForDay.length > 0 ? (
                            <>
                              <span>{t("staff_availability.marked_slots_on_day")}:</span>
                              <ul>
                                <li><strong>{t("staff_availability.available_timings")}:</strong></li>
                                {markedSlotsForDay
                                  .filter(slot => slot.title === 'Available')
                                  .map((slot, index) => (
                                    <li key={index}>
                                      {new Date(slot.start).toLocaleTimeString()} - {new Date(slot.end).toLocaleTimeString()}
                                    </li>
                                  ))}
                                {markedSlotsForDay.filter(slot => slot.title === 'Available').length === 0 && (
                                  <li><span>{t("staff_availability.none")}</span></li>
                                )}

                                <li><strong>{t("staff_availability.unavailable_timings")}:</strong></li>
                                {markedSlotsForDay
                                  .filter(slot => slot.title === 'Unavailable')
                                  .map((slot, index) => (
                                    <li key={index}>
                                      {new Date(slot.start).toLocaleTimeString()} - {new Date(slot.end).toLocaleTimeString()}
                                    </li>
                                  ))}
                                {markedSlotsForDay.filter(slot => slot.title === 'Unavailable').length === 0 && (
                                  <li><span>{t("staff_availability.none")}</span></li>
                                )}
                              </ul>
                            </>
                          ) : (
                            <span>{t("staff_availability.no_slots_marked_past_day")}</span>
                          )}
                        </div>
                      }
                    />
                   
                  </div>
            )}
    </>
  );
};

export default DoctorAvailabilityComponent;
